import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, Link, useParams } from "react-router-dom";
import { ArrowLeft, UserPlus, X } from "lucide-react";
import { handleGet, handleUpdate } from "../../../controllers/controller.all";
import SubmitButton from "../../../components/ui/SubmitButton";
import Select from "react-select";
import { toast } from "react-toastify";
import Loader from "../../template/view.loader";

export default function ReunionUpdate() {
    const { id } = useParams();
    const user = useSelector((state) => state.userReducer?.user);
    const token = useSelector((state) => state.userReducer?.token);
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [admins, setAdmins] = useState([]);
    const [linkMode, setLinkMode] = useState("manual");

    const [formData, setFormData] = useState({
        titre: "",
        date: "",
        heure_debut: "",
        heure_fin: "",
        lieu_lien: "",
        participants_internes: []
    });

    const [selectedInternes, setSelectedInternes] = useState([]);

    const [externeForm, setExterneForm] = useState({ nom: "", contact: "", type_contact: "email" });
    const [externes, setExternes] = useState([]);

    const ecoleId = user?.ecole?._id || user?.ecole;
    const isPremiumOrVip = user?.ecole?.plan === "PREMIUM" || user?.ecole?.plan === "VIP";

    useEffect(() => {
        // Fetch available admins
        handleGet(`/user/s/all/admins/${ecoleId}`, (res) => {
            if (res?.users) {
                const options = res.users.map(u => ({
                    value: u._id,
                    label: `${u.nom} ${u.prenom} ${u.subrole ? `(${u.subrole})` : ""}`
                }));
                setAdmins(options);
                
                // Fetch reunion data
                handleGet(`/reunion/${id}`, (resReu) => {
                    if (resReu.state === "success") {
                        const reu = resReu.reunion;
                        setFormData({
                            titre: reu.titre || "",
                            date: reu.date || "",
                            heure_debut: reu.heure_debut || "",
                            heure_fin: reu.heure_fin || "",
                            lieu_lien: reu.lieu_lien || "",
                            participants_internes: reu.participants_internes || []
                        });
                        setExternes(reu.participants_externes || []);

                        // Map selected options
                        if (reu.participants_internes) {
                            const selected = options.filter(opt => reu.participants_internes.includes(opt.value));
                            setSelectedInternes(selected);
                        }
                    } else {
                        toast.error("Réunion introuvable");
                        navigate("/reunions");
                    }
                    setInitialLoading(false);
                }, token);
            }
        }, token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (selectedOptions) => {
        setSelectedInternes(selectedOptions || []);
        setFormData(prev => ({
            ...prev,
            participants_internes: selectedOptions ? selectedOptions.map(opt => opt.value) : []
        }));
    };

    const addExterne = () => {
        if (!externeForm.nom || !externeForm.contact) {
            toast.warning("Veuillez remplir le nom et le contact de l'invité.");
            return;
        }
        setExternes([...externes, { ...externeForm }]);
        setExterneForm({ nom: "", contact: "", type_contact: "email" });
    };

    const removeExterne = (index) => {
        const newExternes = [...externes];
        newExternes.splice(index, 1);
        setExternes(newExternes);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const payload = {
            ...formData,
            ecole: ecoleId,
            participants_externes: externes
        };

        const res = await handleUpdate(e, `/reunion/${id}`, payload, setLoading, token);
        if (res && res.state === "success") {
            navigate("/reunions");
        }
    };

    if (initialLoading) return <Loader />;

    return (
        <div className="p-6 max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
                <Link to="/reunions" className="p-2 hover:bg-gray-100 rounded-full transition text-gray-600">
                    <ArrowLeft className="w-5 h-5" />
                </Link>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Modifier la Réunion</h1>
                    <p className="text-gray-500">Mettre à jour les informations de la réunion</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6 md:p-8 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="col-span-full">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Titre de la réunion *</label>
                            <input required type="text" name="titre" value={formData.titre} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition" />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                            <input required type="date" name="date" value={formData.date} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition" />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Lieu ou Lien (Visio) *</label>
                            {isPremiumOrVip && (
                                <div className="flex gap-4 mb-2">
                                    <label className="flex items-center gap-2 text-sm text-gray-700">
                                        <input 
                                            type="radio" 
                                            name="linkMode" 
                                            checked={linkMode === "manual"} 
                                            onChange={() => {
                                                setLinkMode("manual");
                                            }} 
                                        /> Saisir manuellement
                                    </label>
                                    <label className="flex items-center gap-2 text-sm text-gray-700">
                                        <input 
                                            type="radio" 
                                            name="linkMode" 
                                            checked={linkMode === "jitsi"} 
                                            onChange={() => {
                                                setLinkMode("jitsi");
                                                setFormData({...formData, lieu_lien: `https://kmeet.infomaniak.com/KalanYoro-Reunion-${Date.now()}`});
                                            }} 
                                        /> Générer un lien Visioconférence
                                    </label>
                                </div>
                            )}
                            <input 
                                required 
                                type="text" 
                                name="lieu_lien" 
                                value={formData.lieu_lien} 
                                onChange={handleChange} 
                                readOnly={linkMode === "jitsi"}
                                className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition ${linkMode === "jitsi" ? "bg-gray-100 text-gray-500 cursor-not-allowed" : ""}`} 
                                placeholder="Ex: Salle des profs ou lien Meet" 
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Heure de début *</label>
                            <input required type="time" name="heure_debut" value={formData.heure_debut} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition" />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Heure de fin *</label>
                            <input required type="time" name="heure_fin" value={formData.heure_fin} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition" />
                        </div>

                        <div className="col-span-full">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Participants Internes (Admins)</label>
                            <Select
                                isMulti
                                options={admins}
                                value={selectedInternes}
                                onChange={handleSelectChange}
                                placeholder="Sélectionner les administrateurs..."
                                className="react-select-container"
                                classNamePrefix="react-select"
                            />
                        </div>
                    </div>

                    <div className="border-t pt-6 mt-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Invités Externes</h3>
                        <div className="flex flex-wrap items-end gap-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <div className="flex-1 min-w-[200px]">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Nom complet</label>
                                <input type="text" value={externeForm.nom} onChange={(e) => setExterneForm({...externeForm, nom: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg" placeholder="Ex: M. Dupont" />
                            </div>
                            <div className="flex-1 min-w-[200px]">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Contact (Email ou Tél)</label>
                                <input type="text" value={externeForm.contact} onChange={(e) => setExterneForm({...externeForm, contact: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg" placeholder="Ex: email@domaine.com" />
                            </div>
                            <div className="w-[150px]">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Mode d'envoi</label>
                                <select value={externeForm.type_contact} onChange={(e) => setExterneForm({...externeForm, type_contact: e.target.value})} className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white">
                                    <option value="email">Email</option>
                                    <option value="sms">SMS</option>
                                </select>
                            </div>
                            <button type="button" onClick={addExterne} className="px-4 py-2 bg-gray-800 text-white font-medium rounded-lg hover:bg-gray-700 transition flex items-center gap-2">
                                <UserPlus className="w-4 h-4" /> Ajouter
                            </button>
                        </div>
                        
                        {externes.length > 0 && (
                            <ul className="mt-4 space-y-2">
                                {externes.map((ext, i) => (
                                    <li key={i} className="flex justify-between items-center bg-white border border-gray-200 p-3 rounded-lg shadow-sm">
                                        <div>
                                            <span className="font-medium text-gray-900">{ext.nom}</span>
                                            <span className="text-gray-500 text-sm ml-2">({ext.contact} - via {ext.type_contact.toUpperCase()})</span>
                                        </div>
                                        <button type="button" onClick={() => removeExterne(i)} className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 rounded transition">
                                            <X className="w-4 h-4" />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
                
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
                    <Link to="/reunions" className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition">
                        Annuler
                    </Link>
                    <SubmitButton 
                        type="submit" 
                        isSubmitting={loading}
                        loadingText="Mise à jour..."
                    >
                        Mettre à jour
                    </SubmitButton>
                </div>
            </form>
        </div>
    );
}
